import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@cargomatic/ui';
import { sluggify } from 'utils/localization/utils';
import { getFormatDateTimeWithRegionalSettings } from 'common/helpers/dateUtils';
import { DocumentDeleteButton } from './DocumentDeleteButton';
import { SignedDocumentLink } from './SignedDocumentLink';
import { FileIcon } from './FileIcon';
import Badge from '../../v2/Badge';

export const DOCUMENT_STATUSES = {
  PENDING: 'PENDING',
  UPLOADED: 'UPLOADED',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
};

export const DOCUMENT_STATUSES_TITLE_MAP = {
  [DOCUMENT_STATUSES.PENDING]: 'Pending',
  [DOCUMENT_STATUSES.UPLOADED]: 'Uploaded',
  [DOCUMENT_STATUSES.UPLOAD_FAILED]: 'Upload failed',
};

export const DocumentNameCell = ({ document }) => {
  const { t } = useTranslation();
  const documentName = t(sluggify(document.name));
  const extension = (
    (document?.key || '').split('.').pop() || ''
  ).toUpperCase();

  return (
    <div className="flex items-center space-x-2">
      <FileIcon extension={extension} />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <SignedDocumentLink
              data-testid="a-f71a9fae"
              documentKey={document.key}
              className="block max-w-[200px] truncate text-left text-blue-600 hover:underline"
            >
              {documentName}
            </SignedDocumentLink>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-md break-all">
            {documentName}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export const DocumentTypeCell = ({ document }) => {
  const { t } = useTranslation();
  return document.type ? t(sluggify(document.type)) : '';
};

export const DocumentDateCell = ({ document }) =>
  document.createdAt
    ? getFormatDateTimeWithRegionalSettings(document.createdAt)
    : null;

export const DocumentSignatoryCell = ({ document, isSent }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center space-x-1">
      {isSent && (
        <Badge dataTestId="Badge-3e5eecae" color="green">
          {t('EMAIL_SENT')}
        </Badge>
      )}
      {document.signatory && !isSent && (
        <Badge dataTestId="Badge-47376021" color="green">
          {document.signatory}
        </Badge>
      )}
    </div>
  );
};

export const DocumentStatusCell = ({ document }) => {
  const { t } = useTranslation();
  if (!document.status) return null;
  return (
    <span className="text-sm text-muted-foreground">
      {t(sluggify(DOCUMENT_STATUSES_TITLE_MAP[document.status] || 'Unknown'))}
    </span>
  );
};

export const DocumentUserCell = ({ document }) =>
  `${document.firstName || ''} ${document.lastName || ''}`.trim();

export const DocumentDeleteCell = ({
  isOrder,
  document,
  deleteDisabled,
  onConfirm,
  onDecline,
}) => {
  if (deleteDisabled) return null;
  return (
    <DocumentDeleteButton
      isOrder={isOrder}
      document={document}
      onConfirm={onConfirm}
      onDecline={onDecline}
    />
  );
};
